import React from "react";
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from "../services/config";
import { Link } from "react-router-dom";
import { Button, Card, CardBody, CardFooter, Divider } from "@heroui/react";
import ContactForm from "../components/contact-form";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import NotFoundPage from "./not-found-page";
import { FeaturedProducts } from "../components/featured-products";
import heroProductsCandles from "../assets/images/hero-products-candles.png";
import heroProductsMassageCandles from "../assets/images/hero-products-massage.png";
import heroProductsDiffusers from "../assets/images/hero-products-diffusers.png";
import heroProductsSashet from "../assets/images/hero-products-sashet.png";
import heroProductsAuto from "../assets/images/hero-products-auto.png";
import heroProductsDecorativeCandles from "../assets/images/hero-products-decorative.png";

interface ProductPageProps {
  productType: string;
  onAddToCart: (productName: string) => void;
  onProductSelect: (productId: number) => void;
}

interface Product {
  id: number;
  name: string;
  scent: string;
  price: number;
  image: string;
}

export const ProductPage: React.FC<ProductPageProps> = ({ productType, onAddToCart, onProductSelect }) => {
  const location = useLocation();

  // Supported product types list — render 404 when an unknown type is requested
  const supportedProductTypes = [
    "candles",
    "massage_candles",
    "diffusers",
    "sachets",
    "decorative_candles",
    "auto_perfumes"
  ];

  // If unknown productType requested, show NotFound page
  if (!supportedProductTypes.includes(productType)) {
    return <NotFoundPage /> as any;
  }

  const getProductTitle = () => {
    switch (productType) {
      case "candles": return "Ароматические свечи";
      case "massage_candles": return "Массажные свечи";
      case "diffusers": return "Диффузоры";
      case "auto_perfumes": return "Автомобильные парфюмы";
      case "sachets": return "Саше";
      case "decorative_candles": return "Декоративные свечи";
      default: return "Продукция";
    }
  };

  const getProductDescription = () => {
    switch (productType) {
      case "candles": 
        return "Наши ароматические свечи созданы вручную из натурального кокосового воска с добавлением премиальных парфюмерных масел. Каждая свеча наполнит ваш дом изысканным ароматом и создаст атмосферу уюта и гармонии.";
      case "diffusers": 
        return "Диффузоры — это элегантный способ наполнить пространство тонким, стойким ароматом без использования открытого огня. Идеальное решение для постоянной ароматизации помещений любого типа.";
      case "sachets": 
        return "Ароматические саше — компактные ароматизаторы для небольших пространств. Идеально подходят для шкафов, гардеробных, автомобилей и других мест, где требуется деликатный аромат.";
      case "massage_candles": 
        return "Наши массажные свечи — это уникальное сочетание ароматерапии и ухода за кожей. При горении они превращаются в теплое масло для массажа, которое питает и увлажняет кожу, создавая неповторимый опыт релаксации и наслаждения.";
      case "decorative_candles": 
        return "Декоративные свечи — это произведения искусства, созданные вручную в уникальных формах и текстурах. Они становятся ярким акцентом в любом интерьере и могут служить как декоративным элементом, так и источником мягкого, атмосферного освещения.";
      case "auto_perfumes":
        return "Автомобильные парфюмы — компактные и стойкие ароматизаторы для салона автомобиля. Разработаны так, чтобы долго сохранять характерный аромат и не мешать во время вождения.";
        default: 
        return "Наша продукция создана из премиальных материалов с использованием натуральных компонентов. Каждое изделие изготавливается вручную с вниманием к деталям.";
    }
  };

  const getProductFeatures = () => {
    switch (productType) {
      case "candles": 
        return [
          { icon: "lucide:flame", title: "Время горения", description: "До 50 часов равномерного горения без копоти" },
          { icon: "lucide:leaf", title: "Натуральный воск", description: "100% натуральный кокосовый воск без парафина" },
          { icon: "lucide:flask-conical", title: "Премиальные масла", description: "Парфюмерные композиции высшего качества" },
          { icon: "lucide:hand", title: "Ручная работа", description: "Каждая свеча создана вручную с вниманием к деталям" }
        ];
      case "diffusers": 
        return [
          { icon: "lucide:clock", title: "Длительность", description: "Аромат сохраняется до 3-4 месяцев" },
          { icon: "lucide:sparkles", title: "Без нагрева", description: "Не требует электричества или открытого огня" },
          { icon: "lucide:flask-conical", title: "Натуральные масла", description: "Высококонцентрированные парфюмерные композиции" },
          { icon: "lucide:recycle", title: "Экологичность", description: "Флаконы из перерабатываемого стекла" }
        ];
      case "sachets": 
        return [
          { icon: "lucide:clock", title: "Стойкость", description: "Сохраняют аромат до 2-3 месяцев" },
          { icon: "lucide:leaf", title: "Натуральный состав", description: "Смесь ароматических трав и масел" },
          { icon: "lucide:package", title: "Компактность", description: "Идеальны для небольших пространств" },
          { icon: "lucide:recycle", title: "Биоразлагаемость", description: "Полностью экологичный продукт" }
        ];
      case "massage_candles": 
        return [
          { icon: "lucide:heart", title: "Двойное назначение", description: "Свеча и массажное масло для тела в одном продукте" },
          { icon: "lucide:leaf", title: "Натуральные масла", description: "Масла ши, кокоса и жожоба для питания кожи" },
          { icon: "lucide:thermometer", title: "Низкая температура плавления", description: "Комфортная температура для нанесения на кожу" },
          { icon: "lucide:sparkles", title: "Ароматерапевтический эффект", description: "Гармонизирующее воздействие на чувства" }
        ];
      case "decorative_candles": 
        return [
          { icon: "lucide:palette", title: "Уникальный дизайн", description: "Каждая свеча — произведение искусства" },
          { icon: "lucide:home", title: "Декоративная функция", description: "Украшение интерьера даже в нежжёном виде" },
          { icon: "lucide:pencil", title: "Ручная работа", description: "Создаются с использованием техник скульптурного литья" },
          { icon: "lucide:eye", title: "Эффектный вид", description: "Привлекают внимание необычной формой и текстурой" }
        ];
      case "auto_perfumes":
        return [
          { icon: "lucide:truck", title: "Компактный формат", description: "Удобный размер для монтажа в салоне автомобиля" },
          { icon: "lucide:clock", title: "Длительная стойкость", description: "Формулы рассчитаны на длительное, равномерное выделение аромата" },
          { icon: "lucide:leaf", title: "Нежные композиции", description: "Сбалансированные ароматы, не раздражающие во время вождения" },
          { icon: "lucide:refresh-ccw", title: "Легкая замена", description: "Картриджи и сменные элементы устанавливаются быстро и безопасно" }
        ];
      default: 
        return [
          { icon: "lucide:leaf", title: "Натуральность", description: "Только экологичные материалы" },
          { icon: "lucide:sparkles", title: "Премиум-качество", description: "Высококачественные компоненты" },
          { icon: "lucide:hand", title: "Ручная работа", description: "Внимание к каждой детали" },
          { icon: "lucide:recycle", title: "Экологичность", description: "Забота об окружающей среде" }
        ];
    }
  };

  const getUsageTips = () => {
    switch (productType) {
      case "candles": 
        return [
          { icon: "lucide:scissors", title: "Подрезайте фитиль", description: "Перед каждым использованием подрезайте фитиль до 5-7 мм" },
          { icon: "lucide:clock", title: "Время горения", description: "Оптимальное время горения 2-4 часа за один раз" },
          { icon: "lucide:alert-triangle", title: "Не оставляйте без присмотра", description: "Никогда не оставляйте горящую свечу без присмотра" },
          { icon: "lucide:wind", title: "Избегайте сквозняков", description: "Размещайте вдали от сквозняков для равномерного горения" }
        ];
      case "diffusers": 
        return [
          { icon: "lucide:move", title: "Переворачивайте палочки", description: "Раз в неделю переворачивайте палочки для усиления аромата" },
          { icon: "lucide:sun", title: "Избегайте прямых солнечных лучей", description: "Размещайте вдали от солнечных лучей и источников тепла" },
          { icon: "lucide:thermometer", title: "Оптимальная температура", description: "Наилучшее распространение аромата при 20-25°C" },
          { icon: "lucide:hand", title: "Осторожное обращение", description: "Избегайте контакта масла с кожей и поверхностями" }
        ];
      case "sachets": 
        return [
          { icon: "lucide:refresh-ccw", title: "Периодически встряхивайте", description: "Для усиления аромата слегка встряхивайте саше" },
          { icon: "lucide:sun", title: "Избегайте влажности", description: "Храните в сухом месте для сохранения аромата" },
          { icon: "lucide:hand", title: "Не вскрывайте", description: "Не нарушайте целостность упаковки" },
          { icon: "lucide:replace", title: "Своевременная замена", description: "Заменяйте каждые 2-3 месяца для сохранения свежести аромата" }
        ];
      case "massage_candles": 
        return [
          { icon: "lucide:flame", title: "Время плавления", description: "Дайте свече гореть 15-20 минут для образования достаточного количества масла" },
          { icon: "lucide:thermometer", title: "Проверьте температуру", description: "Перед нанесением убедитесь, что масло комфортной температуры" },
          { icon: "lucide:hand", title: "Техника нанесения", description: "Аккуратно перелейте небольшое количество масла на кожу и массируйте" },
          { icon: "lucide:alert-triangle", title: "Меры предосторожности", description: "Не наносите на поврежденную кожу и избегайте попадания в глаза" }
        ];
      case "decorative_candles": 
        return [
          { icon: "lucide:sun", title: "Храните правильно", description: "Берегите от прямых солнечных лучей и высоких температур" },
          { icon: "lucide:eye", title: "Используйте как декор", description: "Размещайте в качестве декоративного элемента даже без зажигания" },
          { icon: "lucide:hand", title: "Бережное обращение", description: "Избегайте повреждений уникальной формы и текстуры" },
          { icon: "lucide:flame", title: "При зажигании", description: "Учитывайте форму свечи и выбирайте безопасное место" }
        ];
      case "auto_perfumes":
        return [
          { icon: "lucide:wind", title: "Правильная установка", description: "Устанавливайте автопарфюм согласно инструкции, избегайте прямого попадания на электронику" },
          { icon: "lucide:clock", title: "Ротация аромата", description: "Меняйте положение картриджа для обновления запаха при необходимости" },
          { icon: "lucide:drop", title: "Аккуратная заправка", description: "Заправляйте сменные флаконы на безопасной поверхности, чтобы не повредить обивку" },
          { icon: "lucide:ear", title: "Деликатность", description: "Выбирайте более мягкие композиции для длительных поездок" }
        ];
      default: 
        return [
          { icon: "lucide:info", title: "Следуйте инструкции", description: "Внимательно ознакомьтесь с инструкцией к продукту" },
          { icon: "lucide:alert-triangle", title: "Меры предосторожности", description: "Соблюдайте базовые меры безопасности" },
          { icon: "lucide:sun", title: "Условия хранения", description: "Храните в сухом прохладном месте" },
          { icon: "lucide:hand", title: "Бережное использование", description: "Обращайтесь с продуктом бережно для продления срока службы" }
        ];
    }
  };

  const getUsageAreas = () => {
    switch (productType) {
      case "candles": 
        return [
          { title: "Гостиная", description: "Создайте уютную атмосферу для отдыха и общения с близкими" },
          { title: "Спальня", description: "Расслабляющий аромат для спокойного сна и романтической обстановки" },
          { title: "Ванная комната", description: "Превратите обычные водные процедуры в спа-ритуал" },
          { title: "Рабочий кабинет", description: "Повысьте концентрацию и продуктивность с помощью бодрящих ароматов" }
        ];
      case "diffusers": 
        return [
          { title: "Офисные помещения", description: "Создайте приятную рабочую атмосферу без навязчивого аромата" },
          { title: "Прихожая", description: "Встречайте гостей приятным ароматом с порога" },
          { title: "Гардеробная", description: "Наполните шкафы и гардеробные тонким ароматом свежести" },
          { title: "Спальня", description: "Обеспечьте постоянный легкий аромат для спокойного сна" }
        ];
      case "sachets": 
        return [
          { title: "Шкафы и комоды", description: "Придайте белью и одежде приятный аромат" },
          { title: "Автомобиль", description: "Создайте приятную атмосферу в салоне вашего автомобиля" },
          { title: "Обувные полки", description: "Нейтрализуйте неприятные запахи и добавьте свежести" },
          { title: "Чемоданы и дорожные сумки", description: "Сохраняйте свежесть вещей во время путешествий" }
        ];
      case "massage_candles": 
        return [
          { title: "Спальня", description: "Создайте атмосферу романтики и интимности для особых моментов" },
          { title: "Ванная комната", description: "Превратите принятие ванны в ароматерапевтическую SPA-процедуру" },
          { title: "Массажный кабинет", description: "Профессиональное использование для релаксирующих и ароматических массажей" },
          { title: "Зона отдыха", description: "Уголок релаксации для полного расслабления тела и разума" }
        ];
      case "decorative_candles": 
        return [
          { title: "Гостиная", description: "Стильный декоративный элемент на журнальном столике или полке" },
          { title: "Каминная полка", description: "Эффектное дополнение к интерьеру с камином" },
          { title: "Фотосессия", description: "Оригинальный реквизит для создания атмосферных фотографий" },
          { title: "Подарок", description: "Уникальный презент для ценителей необычных предметов интерьера" }
        ];
      case "auto_perfumes":
        return [
          { title: "Салон автомобиля", description: "Основное применение — создание приятной атмосферы в салоне" },
          { title: "Багажник", description: "Подходит для ароматизации спортивного или хозяйственного отделения" },
          { title: "Кабинеты и гардероб", description: "Может использоваться в небольших помещениях и шкафах" },
          { title: "Подарок", description: "Хороший подарок для автолюбителей" }
        ];
      default: 
        return [
          { title: "Дом", description: "Создайте уютную атмосферу в любом помещении" },
          { title: "Офис", description: "Повысьте продуктивность и настроение в рабочем пространстве" },
          { title: "Личное пространство", description: "Добавьте индивидуальности вашему личному пространству" },
          { title: "Подарок", description: "Идеальный подарок для любого случая" }
        ];
    }
  };

  const getProducts = (): Product[] => {
    switch (productType) {
      case "candles": 
        return [
          { id: 101, name: "Vanilla & Amber", scent: "Ваниль, Амбра, Сандал", price: 2900, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle2" },
          { id: 102, name: "Black Orchid", scent: "Орхидея, Пачули, Ваниль", price: 3200, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle3" },
          { id: 103, name: "Forest Mist", scent: "Хвоя, Мох, Кедр", price: 2800, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle4" },
          { id: 104, name: "Sea Breeze", scent: "Морская соль, Цитрус, Мускус", price: 2700, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle5" },
          { id: 105, name: "Lavender Dreams", scent: "Лаванда, Бергамот, Ваниль", price: 2600, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle6" },
          { id: 106, name: "Spiced Orange", scent: "Апельсин, Корица, Гвоздика", price: 2800, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=candle7" }
        ];
      case "diffusers": 
        return [
          { id: 201, name: "Cedar & Sage", scent: "Кедр, Шалфей, Бергамот", price: 3400, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser1" },
          { id: 202, name: "White Jasmine", scent: "Жасмин, Гардения, Мускус", price: 3600, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser2" },
          { id: 203, name: "Citrus Grove", scent: "Лимон, Бергамот, Вербена", price: 3200, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser3" },
          { id: 204, name: "Amber Wood", scent: "Амбра, Сандал, Пачули", price: 3800, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser4" },
          { id: 205, name: "Fresh Cotton", scent: "Хлопок, Лаванда, Мускус", price: 3300, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser5" },
          { id: 206, name: "Ocean Breeze", scent: "Морская соль, Цитрус, Мята", price: 3500, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=diffuser6" }
        ];
      case "sachets": 
        return [
          { id: 301, name: "Lavender Fields", scent: "Лаванда, Ромашка", price: 1200, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet1" },
          { id: 302, name: "Fresh Linen", scent: "Хлопок, Цитрус", price: 1100, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet2" },
          { id: 303, name: "Cedar & Mint", scent: "Кедр, Мята, Эвкалипт", price: 1300, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet3" },
          { id: 304, name: "Rose Garden", scent: "Роза, Пион, Мускус", price: 1400, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet4" },
          { id: 305, name: "Vanilla Comfort", scent: "Ваниль, Сандал, Мускус", price: 1200, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet5" },
          { id: 306, name: "Citrus Fresh", scent: "Лимон, Апельсин, Бергамот", price: 1100, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=sachet6" }
        ];
      case "massage_candles": 
        return [
          { id: 701, name: "Lavender Relax", scent: "Лаванда, Шалфей, Ромашка", price: 3200, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=massage_candle1" },
          { id: 702, name: "Warm Vanilla", scent: "Ваниль, Кокос, Миндаль", price: 3100, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=massage_candle2" },
          { id: 703, name: "Exotic Ylang", scent: "Иланг-иланг, Бергамот, Жасмин", price: 3400, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=massage_candle3" },
          { id: 704, name: "Citrus Energy", scent: "Апельсин, Лимон, Петитгрейн", price: 3300, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=massage_candle4" },
          { id: 705, name: "Rose & Geranium", scent: "Роза, Герань, Пачули", price: 3500, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=massage_candle5" },
          { id: 706, name: "Sandalwood Ritual", scent: "Сандал, Кедр, Ветивер", price: 3600, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=massage_candle6" }
        ];
      case "decorative_candles": 
        return [
          { id: 901, name: "Geometric Cube", scent: "Без запаха", price: 2500, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=decorative_candle1" },
          { id: 902, name: "Twisted Spiral", scent: "Без запаха", price: 2700, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=decorative_candle2" },
          { id: 903, name: "Sculptural Form", scent: "Без запаха", price: 3000, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=decorative_candle3" },
          { id: 904, name: "Marble Effect", scent: "Без запаха", price: 2900, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=decorative_candle4" },
          { id: 905, name: "Terrazzo Style", scent: "Без запаха", price: 2800, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=decorative_candle5" },
          { id: 906, name: "Organic Shape", scent: "Без запаха", price: 3100, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=decorative_candle6" }
        ];
      case "auto_perfumes":
        return [
          { id: 401, name: "Road Velvet", scent: "Цитрус и древесные ноты", price: 900, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=autoparfume1" },
          { id: 402, name: "Midnight Drive", scent: "Амбра, лаванда", price: 950, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=autoparfume2" },
          { id: 403, name: "Citrus Breeze", scent: "Грейпфрут, бергамот", price: 850, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=autoparfume3" },
          { id: 404, name: "Forest Escape", scent: "Хвоя, кедр", price: 920, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=autoparfume4" },
          { id: 405, name: "Vanilla Drive", scent: "Ваниль, мускус", price: 880, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=autoparfume5" },
          { id: 406, name: "Fresh Cotton", scent: "Хлопок, цитрус", price: 870, image: "https://img.heroui.chat/image/furniture?w=600&h=800&u=autoparfume6" }
        ];
      default: 
        return [];
    }
  };

  // Contact form moved to reusable component `ContactForm`

  const products = getProducts();
  const features = getProductFeatures();
  const usageAreas = getUsageAreas();
  const usageTips = getUsageTips();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>{`${getProductTitle()} — SAFONOVA`}</title>
        <meta name="description" content={getProductDescription()} />
        <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || `/product/${productType}`}`} />
        <meta property="og:title" content={`${getProductTitle()} — SAFONOVA`} />
        <meta property="og:description" content={getProductDescription()} />
        <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || `/product/${productType}`}`} />
      </Helmet>
      {/* Hero Banner */}
      <section 
        className="relative flex items-center py-32 md:py-48"
        style={{
          // Prefer per-product hero image when available in assets
          // Add more images to `heroImages` mapping above and they'll be used automatically
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.16), rgba(0, 0, 0, 0.14)), url('${(() => {
            const heroImages: Record<string, string> = {
              candles: heroProductsCandles,
              massage_candles: heroProductsMassageCandles,
              diffusers: heroProductsDiffusers,
              sachets: heroProductsSashet,
              auto_perfumes: heroProductsAuto,
              decorative_candles: heroProductsDecorativeCandles,
              // Example: add other images like
              // diffusers: require('../assets/images/hero-products-diffusers.png').default,
            };
            return heroImages[productType] ?? `https://img.heroui.chat/image/furniture?w=1920&h=1080&u=${productType}`;
          })()}')`,
          backgroundPosition: "center",
          backgroundSize: "cover"
        }}
      >
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
              {getProductTitle()}
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
              {getProductDescription()}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {features.map((feature, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="border border-divider bg-content1 h-full" disableRipple>
                  <CardBody className="p-6">
                    <div className="bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <Icon icon={feature.icon} className="text-primary" width={24} />
                    </div>
                    <h3 className="text-lg font-medium mb-2">{feature.title}</h3>
                    <p className="text-foreground/70 text-sm">{feature.description}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Usage Areas Section */}
      <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Где <span className="font-medium">использовать</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Наши {getProductTitle().toLowerCase()} идеально подходят для различных пространств и ситуаций
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {usageAreas.map((area, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, margin: "-100px" }}
              >
                <h3 className="text-xl font-medium mb-3">{area.title}</h3>
                <p className="text-foreground/70">{area.description}</p>
                <Divider className="mt-6" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section: use shared FeaturedProducts component (fetches /products/collections/) */}
      <FeaturedProducts onProductSelect={(id: any) => onProductSelect(Number(id))} />

      {/* Usage Tips Section */}
      <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Рекомендации по <span className="font-medium">использованию</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Следуйте этим простым рекомендациям, чтобы получить максимальное удовольствие от использования нашей продукции
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {usageTips.map((tip, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="border border-divider bg-background h-full" disableRipple>
                  <CardBody className="p-6">
                    <div className="bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <Icon icon={tip.icon} className="text-primary" width={24} />
                    </div>
                    <h3 className="text-lg font-medium mb-2">{tip.title}</h3>
                    <p className="text-foreground/70 text-sm">{tip.description}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* <div className="text-center">
            <Button 
              color="primary" 
              variant="flat" 
              size="lg"
              endContent={<Icon icon="lucide:download" width={18} />}
              as="a"
              href="#"
              className="font-medium"
            >
              Скачать PDF-инструкцию
            </Button>
          </div> */}
        </div>
      </section>

      {/* Contact Form Section (reusable) */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-light mb-4">Связаться с <span className="font-medium">нами</span></h2>
              <p className="text-foreground/70">
                Если у вас есть вопросы или вы хотите получить индивидуальное предложение, заполните форму ниже
              </p>
            </div>

            <ContactForm />
          </div>
        </div>
      </section>
    </>
  );
};